import type { RefAttributes } from "react";
import { EditSection } from "./EditSection";
import type { QuestionOrGroup, QuestionProps } from "./Question/types";
import { ReviewSection } from "./ReviewSection";
import { Section as Control } from "./Section";
import { Section as Treatment } from "./FirstNameSectionTitle/Section";
import { CoverGeniusSection } from "./CoverGeniusSection";
import { PersonalisedCoverSection } from "./PersonalisedCoverSection";
import { useExperiment } from "../../../hooks/experiments";

export type SectionElement = HTMLElement;

export type SectionVariant = "stacked-sections";

export type CoreSectionProps = {
  id: string;
  title?: string;
  displayAs?: string;
  preamble?: string;
  preambleName?: string;
  postamble?: string;
  variant?: SectionVariant;
  className?: string;
  submitActionLabel?: string;
  interventions?: Array<string>;
};

// On input (server data props and API responses),
// questions can include groups
export type SectionProps = CoreSectionProps & {
  questions: QuestionOrGroup[];
};

// After flattening, questions are no longer grouped
export type FlattenedSectionProps = CoreSectionProps &
  RefAttributes<SectionElement> & {
    questions: QuestionProps[];
  };

export const Section = ({ ref, ...props }: FlattenedSectionProps) => {
  const { displayAs, ...otherProps } = props;
  const firstNameSectionExperiment = useExperiment(
    "uk2601ShopMoveFirstNameQuestion",
  );

  if (displayAs === "review") {
    return <ReviewSection {...otherProps} ref={ref} />;
  }

  if (displayAs === "edit") {
    return <EditSection {...otherProps} ref={ref} />;
  }

  if (displayAs === "cover_genius") {
    return <CoverGeniusSection {...otherProps} ref={ref} />;
  }

  if (displayAs === "personalised_cover") {
    return <PersonalisedCoverSection {...otherProps} ref={ref} />;
  }

  switch (firstNameSectionExperiment) {
    case "dynamic-property-details-title":
      return <Treatment {...otherProps} ref={ref} />;
    default:
      return <Control {...otherProps} ref={ref} />;
  }
};

Section.displayName = "Section";
